// Ported verbatim from the LeadMailer Design System (components/forms/Input.jsx).
import React from 'react'
import { useFieldStyle } from './Field'
import type { ControlSize } from './Button'

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size' | 'prefix'> {
  size?: ControlSize
  error?: React.ReactNode
  prefix?: React.ReactNode
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void
}

export function Input({ size = 'md', error, prefix, style, onChange, ...rest }: InputProps) {
  const [fs, handlers] = useFieldStyle(error)
  const h = {
    sm: 'var(--control-h-sm)',
    md: 'var(--control-h-md)',
    lg: 'var(--control-h-lg)',
  }[size]

  const inner = (
    <input
      {...handlers}
      onChange={(e) => onChange && onChange(e.target.value, e)}
      style={{
        ...fs,
        height: h,
        padding: prefix ? '0 12px 0 0' : '0 12px',
        border: prefix ? 'none' : fs.border,
        boxShadow: prefix ? 'none' : fs.boxShadow,
        background: prefix ? 'transparent' : fs.background,
        flex: 1,
        ...(!prefix ? style : {}),
      }}
      {...rest}
    />
  )

  if (!prefix) return inner
  return (
    <PrefixShell error={error} h={h} style={style} prefix={prefix}>
      {inner}
    </PrefixShell>
  )
}

function PrefixShell({
  error,
  h,
  prefix,
  style,
  children,
}: {
  error?: React.ReactNode
  h: string
  prefix: React.ReactNode
  style?: React.CSSProperties
  children: React.ReactNode
}) {
  const [focus, setFocus] = React.useState(false)
  return (
    <div
      onFocusCapture={() => setFocus(true)}
      onBlurCapture={() => setFocus(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        height: h,
        background: 'var(--surface-card)',
        border: `1px solid ${
          error ? 'var(--danger)' : focus ? 'var(--border-focus)' : 'var(--border-default)'
        }`,
        borderRadius: 'var(--radius-sm)',
        boxShadow: focus ? 'var(--ring-focus)' : 'var(--shadow-xs)',
        transition: 'box-shadow var(--dur-fast) var(--ease-out)',
        ...style,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '0 0 0 12px',
          color: 'var(--text-muted)',
          fontSize: 'var(--text-sm)',
        }}
      >
        {prefix}
      </span>
      {children}
    </div>
  )
}
